import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import i18next from 'i18next';

import { illegalNameChars, formatValue, sensorSubTypeToTitle, sensorSubTypeToTitlePlural } from '../util/topic.js';
import { groupOutlets } from '../Drivers/GenericPDU.jsx';
import OutletGroupHeader from '../DetailPageComponents/OutletGroupHeader.js';

export function getGroupTypeTitle(t, isShort, groupType, isEdit) {
    const groupTypeTitleKey = "sensor_sub_types." + groupType?.toLowerCase() + (isEdit ? "" : "_new");
    let groupTypeTitle;

    if (isShort) {
        groupTypeTitle = i18next.exists(groupTypeTitleKey + "_short") ? t(groupTypeTitleKey + "_short") : t(groupTypeTitleKey);
    } else {
        groupTypeTitle = t(groupTypeTitleKey);
    }
    return groupTypeTitle;
}


export function getInboxButtonTitle (t, p, isShort, groupType, parentGroupId, isEdit, oldName) {
    let title = "";
    let groupTypeTitle = getGroupTypeTitle(t, isShort, groupType, isEdit);

    if (parentGroupId) {
        const parentGroup = p.getPath(parentGroupId);
        if (parentGroup && parentGroup.name) {
            if (isEdit) {
                title = t("groups.edit__in_", {groupType: groupTypeTitle, parent: parentGroup.name, oldName })
            } else {
                title = t("groups.create_new__in_", {groupType: groupTypeTitle, parent: parentGroup.name });
            }
        }
    }

    if (!title) {
        if (isEdit) {
            title = t("groups.edit__", {groupType: groupTypeTitle, oldName })
        } else {
            title = t("groups.create_new__", {groupType: groupTypeTitle});
        }
    }
    return title;
}

export class InboxComponent extends React.Component {

    constructor(props) {
        super(props);
        this.nameInputRef = React.createRef();
        this.state.open = props.showOpen;
    }

    state = {
        loading: true,
        open: false,
        error: null,
        saveError: null,
        data: null,
        ts: 0,
        subTopicName: null,
        primary: null,
        name: "",
        nameError: "",
        ds: {},
        selected: [],
        parentGroupId: null,
        editOriginalName: "",
        illegalCharsFound: false
    };

    static contextTypes = {
        t: PropTypes.func.isRequired,
        p: PropTypes.object.isRequired
    }

    parentGroupId = undefined;
    subscribedTopics = {};
    subscriptions = [];

    subChanged = () => {
        this.setState({"ts": this.state.ts+1});
    }

    valueChanged = (sensorTopic, value, data, bla, name) => {
            this.setState((state, props) => {
                const update = Object.assign(state.ds);
                const sensorUpdate = state.ds[sensorTopic] ? Object.assign(state.ds[sensorTopic]) : {};
                sensorUpdate[name] = data;
                update[sensorTopic] = sensorUpdate;
                return {ds: update};
        });
    }

    subscribe = (data) => {
        const self = this;
        const subTopic = data.subTopicName;
        let extras = [];
        if (data.primary) {
            extras.push(data.primary);
        }
        if (data.secondaries) {
            const snds = data.secondaries.split(",");
            extras = extras.concat(snds);
        }
        if (extras) {
            self.setState({"extras": extras});
        }

        const primary = data.primary;
        const promises = [], topicsToSubscribe = [];

        // When editing, we have the initially selected topics in state.selected. We mark these as available.
        if (this.props.isEdit) {
            for (let i in this.state.selected) {
                const topic = this.state.selected[i];

                for (let sensorTopic in data.results) {
                    const sensorData = data.results[sensorTopic];
                    if (typeof sensorData !== "string") {
                        for (let portIdx in sensorData) {
                            const portTopic = sensorTopic + "/" + subTopic + "/" + portIdx;
                            if (topic === portTopic && sensorData[portIdx] === 'u') {
                                sensorData[portIdx] = 'a';
                            }
                        }
                    } else {
                        if (data.results[topic] === 'u') {
                            data.results[topic] = 'a';
                        }
                    }
                }
            }
        }

        for (let sensorTopic in data.results) {
            // All sensors and groups themselves are already subscribed from DCPage and main.jsx
            const topic = sensorTopic + (subTopic ? ("/" + subTopic + "/+") : "/+");
            if (this.subscribedTopics[topic] !== undefined) {
                continue;
            }

            this.subscribedTopics[topic] = true;
            promises.push(this.context.p.subscribe(topic, self.subChanged));

            // For rackdevices, we now also need the inlets so we can render the parents
            if (subTopic === 'o') {
                const inletsTopic = sensorTopic + "/i/+";
                if (this.subscribedTopics[inletsTopic] !== undefined) {
                    continue;
                }
    
                this.subscribedTopics[inletsTopic] = true;
                promises.push(this.context.p.subscribe(inletsTopic, self.subChanged));
                promises.push(this.context.p.subscribe(inletsTopic + "/Current", self.subChanged));
            }

            if (extras) {
                for (let i in extras) {
                    const extra = extras[i];
                    let sndSubTopic;
                    if (extra === "val") {
                        sndSubTopic = sensorTopic + "/s/*";
                    } else {
                        sndSubTopic = topic + "/" + extras[i];
                    }
                    promises.push(this.context.p.subscribe(sndSubTopic, self.subChanged));
                }
            }
        }

        Promise.all(promises).then(function (subs) {
            // When editing racks, immediately unsubscribe - we don't want to show live values here because
            // it's too much for most browsers
            if (self.props.groupType === "RACK") {
                for (let i in subs) {
                    subs[i]();
                }
            } else {
                self.subscriptions = self.subscriptions.concat(subs);
            }

            self.setState({"loading": false, "data": data.results, "subTopicName": subTopic, primary});
        });
    }

    componentWillUnmount() {
        for (let i in this.subscriptions) {
            this.subscriptions[i]();
        }
        this.subscribedTopics = {};
        this.subscriptions = [];
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.showOpen && prevProps.showOpen !== prevState.open) {
            this.setState({open: prevProps.showOpen});
        }

        this.checkForUpdateFromProps();
    }

    checkForUpdateFromProps() {
        // When editing, we don't necessarily get the parent group id from the containing page, but we know the
        // id of the group we're editing. So lets get the parent group id from there
        let parentGroupId;
        if (this.props.isEdit) {
            const groupCtrlTopic = this.context.p.getPath("groupctrl/" + this.props.groupId);
            if (!groupCtrlTopic || groupCtrlTopic.parent === undefined) {
                this.setState({"error": this.context.t("groups.error_group_could_not_be_loaded")});
                return;
            }
            parentGroupId = groupCtrlTopic.parent;

            if (this.parentGroupId !== parentGroupId) {
                this.parentGroupId = parentGroupId;

                // We also select all members of the group by default, and set the name
                this.setState({"selected": groupCtrlTopic.include.slice(), "name": groupCtrlTopic.name, editOriginalName: groupCtrlTopic.name});
            }
        } else {
            if (this.props.groupId !== this.parentGroupId) {
                parentGroupId = this.props.groupId;
                this.parentGroupId = parentGroupId;
            }
        }
        return parentGroupId;
    }

    componentDidMount() {
        const self = this;

        if (this.state.open) {
            const p = this.context.p;

            if (p.isAuthenticated()) {
                this.onAuthenticated();
            } else {
                p.addListener("authenticated", this.onAuthenticated);
            }
        }
    }

    onAuthenticated = () => {
        let parentGroupId = this.checkForUpdateFromProps();

        this.setState({loading: true});
        this.loadAvailability(parentGroupId);
    }

    loadAvailability(parentGroupId) {
        const self = this;
        axios.get("/api/group?type=" + this.props.groupType + (parentGroupId ? ("&parent=" + parentGroupId) : "")).then((data, err) => {
            if (err) {
                self.setState({err});
            } else {
                self.subscribe(data.data);
            }
        });
    }

    onCheckedChange(topicRaw, e) {
        let topic = topicRaw;
        if (!Array.isArray(topicRaw)) {
            topic = [topicRaw];
        }
        const isAdd = e.target.checked;

        this.setState((state) => {
            let currentlySelected = state.selected;

            if (isAdd) {
                 currentlySelected = state.selected.concat(topic);
            } else {
                currentlySelected = state.selected.filter(t => topic.indexOf(t) === -1);
            }
            return {"selected": currentlySelected};
        });
        console.log("would set", e.target.checked, topic);
    }

    onCheckedAllChange = (baseTopic, ports, e) => {
        const isAdd = e.target.checked;

        let portList = [];
        for (let portIdx in ports) {
            const topic = baseTopic + "/" + this.state.subTopicName + "/" + portIdx;
            portList.push(topic);
        }
        this.findSensors(baseTopic + "/s");
        portList.add(baseTopic + "/s/+");

        this.setState((state) => {
            let currentlySelected = state.selected;

            for (let i in portList) {
                const topic = portList[i];
                if (isAdd) {
                    if (currentlySelected.indexOf(topic) === -1) {
                        currentlySelected = currentlySelected.concat([topic]);
                    }
                } else {
                    currentlySelected = currentlySelected.filter(t => t !== topic);
                }
            }
            return {"selected": currentlySelected};
        });
    }

    updateName = (e) => {
        const newName = e.target.value;
        const illegalCharsFound = newName.match(illegalNameChars);
        this.setState({
            name: newName,
            "illegalCharsFound": illegalCharsFound
        });
    }

    saveGroup = (e) => {
        const t = this.context.t, self = this;

        e.preventDefault();

        if (!this.state.name) {
            window.alert(t("groups.error_group_name_empty"));
            return false;
        }

        if (this.state.illegalCharsFound) {
            return false;
        }

        // Rackdevice and rack cannot be empty
        if (this.state.selected.length === 0 && this.props.groupType.indexOf("RACK") === 0) {
            const groupType = t("sensor_sub_types." + this.props.groupType?.toLowerCase());
            window.alert(t("groups.error_no___selected", {groupType}));
            return false;
        }

        self.setState({loading: true});
        let update;
        let updatePath = "groupctrl";
        if (this.props.isEdit) {
            update = {"name": self.state.name, "=INCLUDE": this.state.selected};
            updatePath = "groupctrl/" + this.props.groupId;
        } else {
            update = {"name": self.state.name, "type": this.props.groupType, "INCLUDE": this.state.selected};

            if (this.props.groupId) {
                update.parent = this.props.groupId;
            }
        }

        self.context.p.put(updatePath, update)
            .then(function (result) {
                const stateUpdate = {loading: false, open: false, name: "", data: null, selected: []};
                
                // If GroupManager returned the id of the newly created group, open this
                if (result && result.v && result.v.lastId) {
                    // If the new group cannot have children, make it focussed
                    let pathOfNewGroup = (!result.v.hasChildren ? "focus;" : "group;") + result.v.lastId;

                    self.props.lastPath(pathOfNewGroup);
                } else {
                    self.props.lastPath(self.props.isEdit ? "group;" + self.props.groupId : null, true);
                }
                self.setState(stateUpdate);
            },
            function (error) {
                if (error.message == "duplicate:name") {
                    // self.setState({nameError: ""})
                    // self.nameInputRef.current.setCustomValidity(t("groups.error_name_unavailable"));
                    window.alert(t("groups.error_name_unavailable"));
                }

                self.setState({ loading: false, saveError: error.message });
            });
    }

    onToggleInbox = (ev) => {
        const self = this;
        this.setState((state) => {
            if (!state.open) {
                self.loadAvailability(self.parentGroupId);
            }
            return {"open": ~state.open, loading: true};
        });
    }

    findSensors(path) {
        const topic = this.context.p.getPath(path);
        let val = "";
        for (let i in topic) {
            if (topic[i] && topic[i]["type"] && topic[i].val && topic[i].val.v) {
                if (topic[i]["type"] !== "TEMPERATURE" && topic[i]["type"] !== "HUMIDITY") {
                    continue;
                }
                if (val) {
                    val += " / ";
                }
                val += formatValue("val", topic[i].val, false, topic[i]["type"]);
            }
        }
        return val === "" ? "-" : val;
    }

    render() {
        const t = this.context.t;
        // Editing doesn't need the "new" prefix
        let title = getInboxButtonTitle(t, this.context.p, false, this.props.groupType, this.parentGroupId, this.props.isEdit, this.state.editOriginalName);
        if (!this.state.open) {
            return (
                <div className="item inbox" onClick={this.onToggleInbox}>
                    <h4>{title}</h4>
                </div>
            );
        }

        if (this.state.loading && !this.state.error) {
            return (
                <div className="loading-lg">
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div>
                    Couldn't load :(
                </div>
            );
        }

        const self = this;
        const widthPerRoot = 3 + (self.state.extras ? self.state.extras.length : 0);
        const isRackDevice = (this.props.groupType.indexOf("RACKDEVICE") === 0);
        const isRack = !isRackDevice && (this.props.groupType.indexOf("RACK") === 0);

        // Go through all involved sensor's component groups and build column groups
        // from them
        const rowMap = {};
        let numRows = 0;
        const pduNameHeader = [];
        const headers = [];

        const portTitle = sensorSubTypeToTitle[this.state.subTopicName];
        const portTitle_plur = sensorSubTypeToTitlePlural[this.state.subTopicName];

        if (isRackDevice) {
            // pduNameHeader.push((
            //     <th key="sensorName-empty">&nbsp;</th>
            // ));
        } else if (isRack) {
            // TODO: For racks use PDU and inlet name instead of idx
            headers.push((
                <th key="col-idx">{t("general.pdu_name")}</th>
            ));
        } else {
            headers.push((
                <th key="col-idx">{t("groups.col_idx")}</th>
            ));
        }

        function addExtraHeaders(keyBase) {
            if (self.state.extras) {
                for (let i in self.state.extras) {
                    const extra = self.state.extras[i];
                    const translationKey = "topic_titles." + extra?.toLowerCase();
                    let translation = t(translationKey);
                    if (translation === translationKey) {
                        // It was not translated, drop
                        // self.setState({extras: self.state.extras.slice().splice(i, 1)});
                        translation = "";
                    }
                    headers.push((
                        <th key={keyBase + "-" + extra}>{translation}</th>
                    ));
                }
            }
        }

        const p = self.context.p;
        let rowToGroupStart = [];
        let hasOutletGroups = [];

        if (isRackDevice) {
            // Get outlet groups
            for (let rootTopicName in self.state.data) {
                hasOutletGroups[rootTopicName] = false;
                let outletGroups = groupOutlets(p.getPath(rootTopicName + "/o"));
                const groups = [];
                rowToGroupStart[rootTopicName] = groups;
                if (Object.keys(outletGroups).length > 1) {
                    for (let inlet in outletGroups) {
                        const firstOutlet = outletGroups[inlet][0];
                        groups[firstOutlet] = [ inlet, outletGroups[inlet].length];
                        hasOutletGroups[rootTopicName] = true;
                    }
                }
                const rootTopic = self.context.p.getPath(rootTopicName);
                const sensorName = (rootTopic && rootTopic.name) ? rootTopic.name : "-";
                pduNameHeader.push((
                    <th key={"sensorName-" + rootTopicName} colSpan={widthPerRoot + (hasOutletGroups[rootTopicName] ? 1 : 0)}>{"PDU \"" + sensorName + "\""}</th>
                ));
                headers.push((
                        <th key={"col-name-" + rootTopicName} colSpan={3}>{portTitle}</th>
                    ));

                addExtraHeaders("col-" + rootTopicName);
                if (hasOutletGroups[rootTopicName]) {
                    headers.push((
                        <th key={"col-outletgroup-" + rootTopicName}>{t("sensor_sub_types.i")}</th>
                    ));
                }
            }
        } else {
            addExtraHeaders("colh");
        }

        function addRow(rootTopic, list) {
            const thisRowMap = [];
            for (let rowIdx in list) {
                thisRowMap.push(rowIdx);
                // let currentEntry = rowMap[rowIdx];
                // if (!currentEntry) {
                //     currentEntry = {};
                //     rowMap[rowIdx] = currentEntry;
                // }
            }
            rowMap[rootTopic] = thisRowMap;
            if (numRows < thisRowMap.length) {
                numRows = thisRowMap.length;
            }
        }

        function formatRackRows() {
            const rows = [];
            for (let i = 0; i < numRows; i++) {
                const cols = [];
                // Now do the column group for each sensor
                let isFirstCol = true;
                for (let rootTopicName in self.state.data) {
                    const portData = self.state.data[rootTopicName];
                    let portIdx = rowMap[rootTopicName][i];

                    if (!rowMap[rootTopicName][i]) {
                        cols.push((
                            <td key={"portName-" + rootTopicName + "-" + portIdx} colSpan={widthPerRoot + (hasOutletGroups[rootTopicName] ? 1 : 0)} className="empty"></td>
                        ));
                        continue;
                    }

                    const port = portData[portIdx];

                    // Determine if the port is currently selected
                    const topic = rootTopicName + "/" + self.state.subTopicName + "/" + portIdx;
                    const isChecked = self.state.selected.indexOf(topic) !== -1;
                    // If this is unavailable give it the correct class, but only if its not selected
                    const isAvailable = port === "a";
                    const className = isAvailable ? "" : "unavailable bg-gray";

                    // Then add port name
                    const portTopic = p.getPath(topic);
                    const portName = (portTopic && portTopic.name) ? portTopic.name : "-";
                    const portIdxReadable = (portTopic && portTopic.id) ? portTopic.id : +i + 1;
                    const icon = (portTopic && portTopic.type) ? <img className="type" src={"/img/types/" + portTopic.type + ".svg"} /> : <span />;
                    const changeCB = self.onCheckedChange.bind(self, topic);
                    cols.push(
                        <React.Fragment key={"portIdx-fr-" + rootTopicName}>
                            <td key={"portIdx-" + rootTopicName} className={isFirstCol ? className : (className + " border-left")}>
                                <input type="checkbox" disabled={!isAvailable} onChange={changeCB} checked={isChecked} />
                                &nbsp;
                                {portIdxReadable}
                            </td>
                            <td key={"portIcon-" + rootTopicName} className={className}>
                                {icon}
                            </td>
                        </React.Fragment>
                    );

                    cols.push((
                        <td key={"portName-" + rootTopicName + "-" + portIdx} className={className + " name"}>
                            { portName }
                        </td>
                    ));

                    if (self.state.extras) {
                        for (let i in self.state.extras) {
                            const extra = self.state.extras[i];
                            const topic = rootTopicName + "/" + self.state.subTopicName + "/" + portIdx + "/" + extra;
                            const primaryTopic = self.context.p.getPath(topic);
                            const val = formatValue(extra, primaryTopic);
                            cols.push((
                                <td key={extra + "-" + rootTopicName + "-" + portIdx} className={className}>
                                    {val}
                                </td>
                            ));
                        }
                    }
                    const outletGroupInfo = rowToGroupStart[rootTopicName][portIdx];

                    // See if we need to render a new outlet group
                    if (outletGroupInfo !== undefined) {
                        cols.push((
                            <OutletGroupHeader renderPlain={true} key={"groupcol-" + outletGroupInfo[0]} basePath={rootTopicName + "/"} inletField="Current" dataRound={2} unit="A" inletPath={outletGroupInfo[0]} numOutlets={outletGroupInfo[1]} />
                        ));
                    }
                }


                rows.push((
                    <tr key={"inbox-" + i}>
                        {cols}
                    </tr>
                ));
            }

            return rows;
        }

        let rows;
        
        if (isRackDevice) {
            // Rack devices need a different layout
            for (let rootTopic in this.state.data) {
                const ports = this.state.data[rootTopic];
                addRow(rootTopic, ports);
            }
            rows = formatRackRows();
        } else if (isRack) {
            rows = [];
            function checkIfOneAvailable (port) {
                for (let i in port) {
                    if (port[i] === 'a') {
                        return true;
                    }
                }
                return false;
            }

            function checkIfAllAvailable (port) {
                for (let i in port) {
                    if (port[i] === 'u') {
                        return false;
                    }
                }
                return true;
            }

            function checkIfTopicIsSelected (topic, accumulator, currentValue) {
                if (accumulator) { return true; }

                return currentValue.indexOf(topic) === 0;
            }

            for (let topic in this.state.data) {
                let cols = [];
                const port = this.state.data[topic];
                
                // We want to filter out completely unavailable rows unless they're part of this group and we're editing
                if (!this.props.isEdit || !this.state.selected.reduce(checkIfTopicIsSelected.bind(this, topic), false)) {
                    // It's not selected, see if we should ignore it as unavailable
                    if (!port || !checkIfOneAvailable(port)) {
                        continue; 
                    }
                }

                // Then group name
                const deviceTopic = self.context.p.getPath(topic);
                const deviceName = (deviceTopic && deviceTopic.name) ? deviceTopic.name : "-";

                // Now check if all inlets have been selected by this guy
                let allSelected = true;
                for (let i in port) {
                    const inletTopic = topic + "/" + this.state.subTopicName + "/" + i;
                    if (this.state.selected.indexOf(inletTopic) === -1) {
                        allSelected = false;
                    }
                }

                let isAvailable = allSelected || checkIfAllAvailable(port);
                let className = isAvailable ? "" : "unavailable";
                
                // See how many inlet rows we will have
                let numRows = 0, numRoots = 0;
                for (let inletIdx in port) {
                    const inletTopic = topic + "/" + this.state.subTopicName + "/" + inletIdx;

                    const portTopic = self.context.p.getPath(inletTopic);
                    if (!portTopic) {
                        continue;
                    }

                    if (!portTopic.parent) {
                        numRoots++;
                        // Now one row for each root
                        numRows++;
                    }
                }

                let isFirstRow = true;

                // Now we render the inlets. Root inlets first (there should rarely ever be more than one?)
                for (let inletIdx in port) {
                    const inletTopic = topic + "/" + this.state.subTopicName + "/" + inletIdx;
                    const wildcardTopic = topic + "/" + this.state.subTopicName + "/+";

                    const portTopic = self.context.p.getPath(inletTopic);
                    if (portTopic === null) {
                        continue;
                    }

                    // Skip if it's not a root or phase inlet
                    if (portTopic.parent || +inletIdx > 3) {
                        continue;
                    }
                    
                    const topicsToBind = [inletTopic];
                    let phaseCurrent = [];

                    // Now find all child inlets
                    for (let childInletIdx in port) {
                        const childPortTopic = self.context.p.getPath(topic + "/" + this.state.subTopicName + "/" + childInletIdx);

                        // If it's not a direct child, check one parent level
                        if (childPortTopic.parent && childPortTopic.parent !== "i/" + inletIdx) {
                            const parentPortTopic = self.context.p.getPath(topic + "/" + childPortTopic.parent);
                            if (parentPortTopic !== "i/" + inletIdx) {
                                continue;
                            }
                        }

                        // Skip if it's not a phase of this port
                        if (childPortTopic === null || childPortTopic.parent !== "i/" + inletIdx || !childPortTopic.Current) {
                            continue;
                        }

                        if (childPortTopic.phase) {
                            // And store their currents by phase
                            phaseCurrent.push(childPortTopic.phase + ": " + formatValue("Current", childPortTopic.Current));
                        }
                        topicsToBind.push(topic + "/" + this.state.subTopicName + "/" + childInletIdx);
                    }

                    const portName = (portTopic && portTopic.name) ? portTopic.name : "-";
                    // Determine if the port is currently selected
                    const isChecked = self.state.selected.indexOf(inletTopic) !== -1;
                    // If this is unavailable give it the correct class, but only if its not selected (because when editing all initially
                    // selected ports while be unavailable as well).
                    isAvailable = isChecked || port[inletIdx] === "a";
                    className = isAvailable ? "" : "unavailable";

                    // If we have more than one root inlet per PDU, add the inlet name in brackets
                    const rowTitle = numRoots > 1 ? (deviceName + " (" + portName + ")") : deviceName;

                    cols.push((
                        <td key={"portName-" + inletTopic} className={className}>
                            <input type="checkbox" disabled={!isAvailable} onChange={self.onCheckedChange.bind(self, topicsToBind)} checked={isChecked} />
                            {" " + rowTitle}
                        </td>
                    ));

                    if (self.state.extras) {
                        for (let i in self.state.extras) {
                            const extra = self.state.extras[i];
                            let val;
                            if (extra === "val" && isFirstRow) {
                                val = self.findSensors(topic + "/s");
                                // Also bind all sensors
                                topicsToBind.push(topic + "/s/+");
                                // console.log("would love to find sensor", extra, topic);
                            } else {
                                const extraTopic = portTopic[extra];
                                val = formatValue(extra, extraTopic);
                                if (extra === "Current" && phaseCurrent) {
                                    val = (<>
                                        { val }
                                        <br />
                                        { phaseCurrent.join(" / ") }
                                    </>
                                    );
                                }
                            }
                            cols.push((
                                <td key={extra + "-" + topic} className={className}>
                                    {val}
                                </td>
                            ));
                        }
                    }
                    isFirstRow = false;
                    rows.push((
                        <tr key={"inbox-" + inletTopic}>
                            {cols}
                        </tr>
                    ));
                    cols = [
                    ];
                }
            }
        } else {
            rows = [];
            for (let topic in this.state.data) {
                const cols = [];
                const port = this.state.data[topic];
                if (port !== "a") {
                    continue;
                }
                // Then group name
                const portTopic = self.context.p.getPath(topic);
                const portName = (portTopic && portTopic.name) ? portTopic.name : "-";

                // Determine if the port is currently selected
                const isChecked = self.state.selected.indexOf(topic) !== -1;
                // If this is unavailable give it the correct class, but only if its not selected (because when editing all initially
                // selected ports while be unavailable as well).
                const isAvailable = isChecked || port === "a";
                const className = isAvailable ? "" : "unavailable";

                cols.push((
                    <td key={"portName-" + topic} className={className}>
                        <input type="checkbox" disabled={!isAvailable} onChange={self.onCheckedChange.bind(self, topic)} checked={isChecked} />
                        {" " + portName}
                    </td>
                ));

                if (self.state.extras) {
                    for (let i in self.state.extras) {
                        const extra = self.state.extras[i];
                        const extraTopic = portTopic[extra];
                        const val = formatValue(extra, extraTopic);
                        cols.push((
                            <td key={extra + "-" + topic} className={className}>
                                {val}
                            </td>
                        ));
                    }
                }
                rows.push((
                    <tr key={"inbox-" + topic}>
                        {cols}
                    </tr>
                ));
            }
        }
        

        let buttonTitle = t("groups." + (this.props.isEdit ? "save" : "create"));

        // TODO: only show headers and pdu headers if rows aren't empty
        // TODO: only show pduheaders if they exist!
        const pduNameHeaderRow = pduNameHeader ? (
                <tr className="pdu-header">
                    { pduNameHeader }
                </tr>
            ) : null;

        let table;

        if (rows && rows.length > 0) {
            table = (
                <table cellSpacing={0} cellPadding={0}>
                    <thead>
                        { pduNameHeaderRow }
                        <tr>
                            { headers }
                        </tr>
                    </thead>
                    <tbody>
                        { rows }
                    </tbody>
                </table>
            );
        } else {
            table = this.context.t("groups.no__are_available", {groupType: portTitle_plur});
        }

        let inputGroupClasses = "input-group";
        let inputButtonClasses = "btn input-group-btn";

        if (this.state.illegalCharsFound) {
            inputGroupClasses += " has-error";
            buttonTitle = this.context.t("details_configuration.button_error_illegal_chars", {chars: this.state.illegalCharsFound.join(", ")});
            inputButtonClasses += " btn-error";
        } else {
            if (!this.state.name) {
                buttonTitle = this.context.t("groups.button_enter_name");
            } else {
                inputButtonClasses += " btn-primary";
            }
        }

        let inputAttributes = {
            type: "text",
            onChange: this.updateName,
            autoFocus: this.props.isEdit ? false : "autofocus",
            required: true
        };

        return (
            <div className="item inbox">
                <div className="inner">
                    <div className="columns">
                        <h2 className="column col-4">{title}</h2>
                        <form className="column col-8">
                            <div className={inputGroupClasses}>
                                <span className="input-group-addon">{t('groups.group_name')}</span>
                                <input {...inputAttributes} value={this.state.name} className="form-input" ref={this.nameInputRef} />
                                <button className={inputButtonClasses} disabled={!!this.state.illegalCharsFound} onClick={this.saveGroup}>{buttonTitle}</button>
                            </div>
                            
                        </form>
                    </div>
                    {table}

                </div>
            </div>
        );
    }
}

InboxComponent.propTypes = {
    groupType: PropTypes.string.isRequired,
    groupId: PropTypes.string,
    isEdit: PropTypes.bool.isRequired,
    subscribeSensor: PropTypes.func.isRequired,
    lastPath: PropTypes.func.isRequired,
    showOpen: PropTypes.bool
}; 